import React, { useCallback, useEffect, useMemo } from 'react';
import { Platform } from 'react-native';
import HtmlPlayablePreview from '@/components/Coworker/HtmlPlayablePreview';
import { filesApi } from '@/lib/api/files';
import { openMessageFile } from '@/constants/utils/files';

// A fenced HTML response has no File yet. Create one only when the user opens
// the artifact, so its source, download and editing actions share the usual viewer.
export default function InlineHtmlPreview({ html, conversationId, isFromTemporaryChat = false }) {
  const source = useMemo(() => ({ active: true, file: null }), [html, conversationId, isFromTemporaryChat]);
  useEffect(() => {
    source.active = true;
    return () => { source.active = false; };
  }, [source]);

  const openArtifact = useCallback(async () => {
    if (!source.file) {
      const name = 'preview.html';
      let file;
      let cleanup;
      try {
        if (Platform.OS === 'web') {
          file = new File([html], name, { type: 'text/html' });
        } else {
          const FileSystem = await import('expo-file-system');
          const uri = `${FileSystem.cacheDirectory}html-artifact-${Date.now()}-${Math.random().toString(36).slice(2)}.html`;
          cleanup = () => FileSystem.deleteAsync(uri, { idempotent: true });
          await FileSystem.writeAsStringAsync(uri, html);
          file = { uri, name, type: 'text/html' };
        }
        const uploaded = await filesApi.uploadFile({ file, name, conversationId, isFromTemporaryChat });
        if (!uploaded?.file?.cdnPath) throw new Error('HTML artifact was not saved');
        source.file = { ...uploaded.file, conversationId };
      } finally {
        if (cleanup) await cleanup().catch(() => {});
      }
    }
    // Navigating away while an upload is pending must not open the old chat's file.
    if (source.active) openMessageFile(source.file);
  }, [html, conversationId, isFromTemporaryChat, source]);

  return <HtmlPlayablePreview name="preview.html" sourceKey={html} loadHtml={() => html} onExpand={openArtifact} expandLabel="Open artifact" />;
}
